import React, { useState, useRef, Suspense, useEffect } from 'react';
import Spinner from 'design-system/Spinner';

import { Dialog, DialogTitle, DialogContent, DialogActions } from 'design-system';
import { makeStyles } from 'design-system/product/theme';

import TaskComponent from 'react-scale/components/Task/TaskComponent';
import { ShortcutsProvider } from 'react-scale/hooks/useShortcut';
import { TaskStatus } from '@scale/shared/defaults';
import Alert from 'design-system/Alert';
import { Button } from 'design-system/Button';

import { sampleTask } from '@scale/shared/selfServe/validationHelpers';
import API from '@scale/frontend-lib/dashboard-api';
import { useQueryClient } from 'react-query';
import _ from 'lodash';
import { useInstructionTaskData } from 'react-scale/dashboard.scale.com/components/SelfServe/Instructions/hooks';
import ErrorBoundary from 'dashboard/components/SelfServe/TaskSetup/RapidTaskErrorBoundary';
import useUnprocessedAttachments from 'react-scale/studio/hooks/useUnprocessedAttachments';
import { CAPTION_PLACEHOLDER } from './ExampleLabelModal';
import { useProjectInstructionContext } from 'react-scale/components/scalers/instruction/projectInstructionContext';

interface ExampleViewerProps {
  modalOpen: boolean;
  taskInfo: $FIXME;
  taskId: string;
  readOnly: boolean;
  setModalOpen: (arg: boolean) => void;
  hasOpenedExample?: (taskId: string, isGoodExample: boolean) => void;
  projectTemplateId?: string;
  updateCaption?: (c: string) => void;
  tasksWithProcessingAnnotationsResponses?: Record<string, any>;
}

const ExampleViewer: React.FC<ExampleViewerProps> = ({
  modalOpen,
  taskInfo,
  taskId,
  readOnly,
  setModalOpen,
  hasOpenedExample,
  projectTemplateId,
  updateCaption,
  tasksWithProcessingAnnotationsResponses,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const taskRef = useRef<$FIXME>();
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [taskProps, setTaskProps] = useState<any>(undefined);
  const { data: taskData } = useInstructionTaskData(taskId, readOnly, projectTemplateId, true);
  const { isMobileView, enableSidePanel } = useProjectInstructionContext();
  const { goodExample, caption } = taskInfo;
  const [currentCaption, setCurrentCaption] = useState(caption);
  const { params, response, type, projectId, status } = taskData;
  const queryClient = useQueryClient();
  const { fetchUnprocessedAttachments } = useUnprocessedAttachments();

  const unableToShowExample = isMobileView && !enableSidePanel;
  const allowToEdit = !readOnly && taskProps && status === TaskStatus.Completed;
  useEffect(() => {
    let canceled = false;
    async function fetchData() {
      if (!loading && params && type && projectId) {
        setTaskProps(undefined);
        const taskParams = params;

        if (
          taskParams &&
          taskParams.attachments &&
          taskParams.attachments.length > 0 &&
          _.every(taskParams.attachments, a => a.type === 'unprocessed')
        ) {
          taskParams.attachments = await fetchUnprocessedAttachments(taskParams.attachments ?? []);
        }

        if (!canceled) {
          if (hasOpenedExample) {
            hasOpenedExample(taskId, goodExample);
          }

          setTaskProps({
            ...sampleTask,
            assignment: {
              params: taskParams,
              projectId,
              type,
              featureFlags: {
                canLeaveInlineComments: !readOnly,
                canSeeInlineComments: true,
              },
              initialInlineComments: response?.inlineComments,
            },
            type,
            params: taskParams,
            fixResponse: response || tasksWithProcessingAnnotationsResponses?.[taskId],
            timerDisabled: true,
            shouldWarnOnExit: false,
            readOnly: !allowToEdit,
            editable: allowToEdit,
            isCustomer: true,
            showHeader: false,
            isTaxonomyDisplay: true,
            showFooter: false,
          });
        }
      }
    }

    fetchData();
    return function cleanup() {
      canceled = true;
    };
  }, [taskInfo]);

  const handleSuccess = () => {
    setSuccessMsg('Task updated');
    setTimeout(() => setSuccessMsg(''), 2000);
  };

  const handleError = () => {
    setErrorMsg('Task update failed');
    setTimeout(() => setErrorMsg(''), 2000);
  };

  const onUpdate = async () => {
    setSuccessMsg('');
    setErrorMsg('');
    setLoading(true);
    // update the actual task
    if (taskRef.current) {
      // get current response
      try {
        const response = await taskRef.current.getReviewChanges();
        const res = await API.selfserve.updateInstructionsExample({ task: taskId, response });
        queryClient.invalidateQueries(['use-instruction-task-data', taskId]);
        if (res.success) {
          if (updateCaption) updateCaption(currentCaption);
          setTaskProps({ ...taskProps, fixResponse: response });
          handleSuccess();
        } else {
          handleError();
        }
      } catch (e) {
        handleError();
      }
    }
    setLoading(false);
  };

  const close = (event?: any) => {
    // prevent ESC from closing modal
    if (event?.type === 'keydown' && event?.keyCode === 27) return;
    setModalOpen(false);
  };

  return (
    <Dialog
      classes={{ root: readOnly ? classes.zIndexHigh : classes.zIndexLow, paper: classes.dialog }}
      maxWidth="lg"
      open={modalOpen}
      onClose={close}
    >
      <DialogTitle>
        <div className={classes.title}>
          Example{' '}
          <span className={classes.closeButton} onClick={close}>
            &#10005;
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        {unableToShowExample && (
          <div>example doesn't support for small screen, please view examples on desktop</div>
        )}
        {!taskProps && <h6>No Images to display...</h6>}
        {taskProps && status !== TaskStatus.Completed && (
          <Alert severity="info" style={{ marginTop: '16px', marginBottom: '6px' }}>
            Annotations are still being processed
          </Alert>
        )}
        {errorMsg.length > 0 && (
          <Alert severity="error" style={{ marginTop: '2px', marginBottom: '12px' }}>
            {errorMsg}
          </Alert>
        )}
        {successMsg.length > 0 && (
          <Alert severity="success" style={{ marginTop: '2px', marginBottom: '12px' }}>
            {successMsg}
          </Alert>
        )}
        {!unableToShowExample && taskProps && (
          <>
            <ErrorBoundary>
              <Suspense fallback={<Spinner style={{ paddingBottom: '16px' }} />}>
                <div className="component">
                  <ShortcutsProvider>
                    <TaskComponent
                      taskType={taskProps.type}
                      taskProps={taskProps}
                      taskRef={taskRef}
                      isTaxonomyDisplay={true}
                    />
                  </ShortcutsProvider>
                </div>
              </Suspense>
            </ErrorBoundary>
          </>
        )}

        <style jsx global>{`
          .component {
            height: 540px;
          }
          .component,
          .component .task {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            min-width: 0;
            width: 100%;
            overflow: auto;
          }
          .task__splitpane {
            position: relative;
            overflow: auto;
          }
        `}</style>

        {!readOnly && (
          <div className={classes.captionContainer}>
            <p className={classes.captionHeader}>Caption</p>
            <input
              value={currentCaption}
              placeholder={CAPTION_PLACEHOLDER}
              className={classes.editableCaption}
              onFocus={e => (e.target.placeholder = '')}
              onBlur={e => (e.target.placeholder = CAPTION_PLACEHOLDER)}
              onChange={e => {
                e.preventDefault();
                setCurrentCaption(e.target.value);
              }}
            />
          </div>
        )}

        {caption && readOnly && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'center',
              background: 'white',
              border: 0,
              fontSize: '16px',
              width: '100%',
            }}
          >
            {caption}
          </div>
        )}
      </DialogContent>
      {allowToEdit ? (
        <DialogActions>
          <Button onClick={() => onUpdate()} disabled={loading || !allowToEdit} type="primary">
            Update
          </Button>
        </DialogActions>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

const useStyles = makeStyles({
  dialog: {
    width: '100%',
    overflow: 'hidden',
    margin: '3% auto 20px',
  },
  zIndexHigh: {
    zIndex: '2000 !important' as any,
  },
  zIndexLow: {
    zIndex: '110 !important' as any,
  },
  title: {
    textAlign: 'center',
  },

  closeButton: {
    position: 'absolute',
    right: 20,
    top: 15,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  editableCaption: {
    opacity: 0.9,
    marginTop: '0px',
    textAlign: 'center',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    width: '100%',
  },
  captionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  captionHeader: {
    width: '100%',
    lineHeight: 1,
    color: 'darkslategray',
    marginBottom: '8px',
  },
});

export default ExampleViewer;
